import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDateRange } from "@shared/hooks/useDateRange";
import { useEvaluations } from "@features/evaluation/queries";
import { useScheduling } from "@features/patients/queries";
import { computeDashboardKpis } from "./kpis";
import { today } from "@shared/utils/date";
import { HttpError } from "@shared/http/client";

type KpiTileProps = {
  icon: string;
  value: number;
  label: string;
  tone?: "dark" | "green" | "warn" | "danger" | "";
};

function KpiTile({ icon, value, label, tone = "" }: KpiTileProps) {
  return (
    <div className={`kpi-card ${tone}`}>
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-label">{label}</div>
    </div>
  );
}

export function DashboardPage() {
  const { range, setRange } = useDateRange();
  const navigate = useNavigate();

  // Últimas valoraciones — respeta el filtro de fecha del user.
  const {
    data: evalsData,
    isLoading: evalsLoading,
    isFetching: evalsFetching,
    error: evalsError,
  } = useEvaluations({
    from: range.from || undefined,
    to:   range.to   || undefined,
    page: 1,
    limit: 100,
  });

  // Todas las valoraciones de hoy — para KPIs (no depende del filtro).
  const todayIso = today();
  const { data: todayEvalsData } = useEvaluations({ from: todayIso, to: todayIso, page: 1, limit: 500 });

  // Total de pendientes (para el tile ⏳).
  const { data: pendData } = useScheduling({ page: 1, limit: 1 });

  const kpis = useMemo(
    () => computeDashboardKpis(todayEvalsData?.items ?? [], pendData?.total ?? 0, todayIso),
    [todayEvalsData, pendData, todayIso],
  );

  const evaluaciones = evalsData?.items ?? [];

  const setToday = () => setRange({ from: todayIso, to: todayIso });
  const clearRange = () => setRange({ from: "", to: "" });

  return (
    <div className="p-24">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Resumen de actividad de valoraciones preoperatorias</p>
      </div>

      <div className="kpi-grid">
        <KpiTile icon="⏳" value={kpis.pendientes}    label="Pendientes"      tone="dark"   />
        <KpiTile icon="📋" value={kpis.valoradosHoy}  label="Valorados hoy"                />
        <KpiTile icon="✅" value={kpis.aptosHoy}      label="Aptos hoy"       tone="green"  />
        <KpiTile icon="🔴" value={kpis.reprogClinica} label="Reprog. clínica" tone="danger" />
        <KpiTile icon="🟡" value={kpis.reprogAdmin}   label="Reprog. adm."    tone="warn"   />
      </div>

      <div className="card mt-24">
        <div className="card-header">
          <div className="card-title">📋 Últimas valoraciones del día</div>
          <div className="date-filters">
            <div className="field-group">
              <input
                type="date"
                value={range.from}
                onChange={(e) => setRange({ ...range, from: e.target.value })}
                aria-label="Desde"
              />
            </div>
            <div className="field-group">
              <input
                type="date"
                value={range.to}
                onChange={(e) => setRange({ ...range, to: e.target.value })}
                aria-label="Hasta"
              />
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={setToday}>Hoy</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={clearRange}>Todo</button>
          </div>
        </div>

        {evalsError instanceof HttpError && (
          <div className="alert-inline alert-inline--danger">Error cargando historial: {evalsError.message}</div>
        )}
        {(evalsLoading || evalsFetching) && (
          <div className="text-muted p-12">{evalsLoading ? "Cargando…" : "Actualizando…"}</div>
        )}

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Paciente</th>
                <th>Procedimiento</th>
                <th>Fecha Cx</th>
                <th>Programó</th>
                <th>Evaluó</th>
                <th>Estado Adm.</th>
                <th>Resultado Clínico</th>
                <th>Decisión Final</th>
                <th style={{ textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {evaluaciones.length === 0 && !evalsLoading && (
                <tr>
                  <td colSpan={10} className="text-muted" style={{ padding: 24, textAlign: "center" }}>
                    No hay valoraciones en el rango seleccionado.
                    {range.from || range.to ? " Prueba con 'Todo' para ver el histórico." : ""}
                  </td>
                </tr>
              )}
              {evaluaciones.map((e) => (
                <tr key={e.id}>
                  <td>{e.doc}</td>
                  <td>{e.nombre}</td>
                  <td>{e.cirugia}</td>
                  <td>{e.fecha || e.fechaRegistro}</td>
                  <td>{e.programador}</td>
                  <td>{e.evaluadorNombre || e.evaluador}</td>
                  <td>{e.estadoAdministrativo}</td>
                  <td>{e.resultadoClinico}</td>
                  <td>{e.accionFinal}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => navigate(`/historial?doc=${encodeURIComponent(e.doc || "")}`)}
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
